import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AttachmentMeta = {
  id: string;
  name: string;
  size: number;
  mime: string;
  kind: 'image' | 'file';
};

export type ComposerDraft = {
  key: string;
  text: string;
  attachments: AttachmentMeta[];
  updatedAt: number;
};

type ComposerDraftsState = {
  byKey: Record<string, ComposerDraft | undefined>;
};

const initialState: ComposerDraftsState = {
  byKey: {},
};

const ensureDraft = (state: ComposerDraftsState, key: string): ComposerDraft => {
  const existing = state.byKey[key];
  if (existing) return existing;
  const draft: ComposerDraft = { key, text: '', attachments: [], updatedAt: Date.now() };
  state.byKey[key] = draft;
  return draft;
};

export const composerDraftsSlice = createSlice({
  name: 'composerDrafts',
  initialState,
  reducers: {
    upsertDraftFromStorage: (
      state,
      action: PayloadAction<{ key: string; text: string; updatedAt?: number }>,
    ) => {
      const { key, text, updatedAt } = action.payload;
      const draft = ensureDraft(state, key);
      const nextUpdatedAt = updatedAt ?? Date.now();
      if (!draft.text || nextUpdatedAt >= draft.updatedAt) {
        draft.text = text ?? '';
        draft.updatedAt = nextUpdatedAt;
      }
    },

    setText: (state, action: PayloadAction<{ key: string; text: string }>) => {
      const { key, text } = action.payload;
      const draft = ensureDraft(state, key);
      draft.text = text;
      draft.updatedAt = Date.now();
    },

    addAttachments: (state, action: PayloadAction<{ key: string; items: AttachmentMeta[] }>) => {
      const { key, items } = action.payload;
      const draft = ensureDraft(state, key);
      const existingIds = new Set(draft.attachments.map((a) => a.id));
      const next = items.filter((i) => !existingIds.has(i.id));
      draft.attachments = [...draft.attachments, ...next];
      draft.updatedAt = Date.now();
    },

    removeAttachment: (state, action: PayloadAction<{ key: string; id: string }>) => {
      const { key, id } = action.payload;
      const draft = ensureDraft(state, key);
      draft.attachments = draft.attachments.filter((a) => a.id !== id);
      draft.updatedAt = Date.now();
    },

    clearDraft: (state, action: PayloadAction<{ key: string }>) => {
      delete state.byKey[action.payload.key];
    },
  },
});

export const { upsertDraftFromStorage, setText, addAttachments, removeAttachment, clearDraft } =
  composerDraftsSlice.actions;

export default composerDraftsSlice.reducer;
